import React, { useEffect, useState, useRef } from 'react';
import Modal from 'react-responsive-modal';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.module.css';
import { useFormik } from 'formik';
import { Checkbox, Input, Select, Textarea, CreatableSelect, MultiSelect } from '../../components/ModalInputs';
import * as Yup from 'yup';
import ModalFooter from '../../components/ModalFooter/ModalFooter';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import moment from 'moment';
import Counter from '../../components/Counter'
import {
  createPublications,
  getPublicationForms,
  getPublicationSubject,
  updatePublications,
  getSinglePublication,
  getPublicationFiles,
  getPublicationAddAuthor,
  uploadPublicationFile,
  deletePublicationFile,
  downloadPublicationFile,
  previewPublicationFile,
} from '../../actions/publication';
import { getTeachersList } from '../../actions/common';
import TeacherModal from './EnrollModal/TeacherModal';

// Полугодие: 1 — I, 2 — II (опционально). Значения совпадают с API (SeasonType).
const arrSeason = [
  { name: 'I полугодие', value: 1 },
  { name: 'II полугодие', value: 2 },
];
// Рецензия: значение = текст (ReviewType).
const arrReview = [
  { name: 'нет', value: 'нет' },
  { name: 'внутренняя', value: 'внутренняя' },
  { name: 'внешняя', value: 'внешняя' },
];
// Уровень: значение = текст (PublicationLevelType).
const arrLevel = [
  { name: 'интернет', value: 'интернет' },
  { name: 'училище', value: 'училище' },
  { name: 'окружной', value: 'окружной' },
  { name: 'муниципальный', value: 'муниципальный' },
  { name: 'региональный', value: 'региональный' },
  { name: 'МО РФ', value: 'МО РФ' },
  { name: 'федеральный', value: 'федеральный' },
  { name: 'международный', value: 'международный' },
];
// Наукометрические базы: мультивыбор (BasesItemType).
const arrBases = [
  { value: 'ринц', label: 'РИНЦ' },
  { value: 'вак', label: 'ВАК' },
  { value: 'scopus', label: 'Scopus' },
  { value: 'wos', label: 'WoS' },
];

// В общем стиле .creatableSelect к контролу применён transform: scale(.9) с точкой
// трансформации по центру — на широком контроле это даёт большой отступ слева.
// Сдвигаем точку трансформации к левому краю, чтобы текст прижимался влево.
const selectLeftOrigin = {
  control: (base) => ({ ...base, transformOrigin: 'left center' }),
};

// Обязательные поля (ТЗ п.5): вид, название, год, минимум 1 автор.
const validationSchema = Yup.object({
  form: Yup.string().required('Обязательное поле'),
  name: Yup.string()
    .required('Обязательное поле')
    .max(150, 'Должно быть не более 150 символов'),
  pub_year: Yup.number()
    .required('Укажите год публикации')
    .min(1, 'Укажите год публикации'),
});

const emptyValues = {
  form: '',
  name: '',
  publish_date: '',
  pub_year: Number(moment().format('YYYY')),
  season: '',
  subject: '',
  review: 'нет',
  has_copy: false,
  level: 'интернет',
  volume: 0,
  volume_pp: 0,
  hours: 0,
  place: '',
  result_num: '',
  bases: [],
  site: '',
};

export default function ModalComponent({ data, closeModal, height, addMode, filters }) {

  const dispatch = useDispatch();

  const { publicationsSubject, publicationForms, authorsToAdd, singlePublication, publicationFiles } = useSelector(state => state.publications);

  // Единый упорядоченный список авторов: { mid, fio, is_external }
  const [authors, setAuthors] = useState([]);
  const [openAuthorModal, setOpenAuthorModal] = useState(false);
  const [incomeAuthor, setIncomeAuthor] = useState('');
  const [inAuthor, setInAuthor] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  // Файлы, выбранные до сохранения новой публикации (загрузятся после создания)
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(getPublicationSubject());
    dispatch(getPublicationForms());
    dispatch(getTeachersList());
    dispatch(getPublicationAddAuthor());
    if (data?.id) {
      dispatch(getSinglePublication(data.id));
      dispatch(getPublicationFiles(data.id));
    }
  }, []);

  // Инициализация авторов из загруженной записи
  useEffect(() => {
    if (!addMode && singlePublication?.id === data?.id && Array.isArray(singlePublication?.authors_list)) {
      setAuthors(singlePublication.authors_list.map(a => ({
        mid: a.mid,
        fio: a.fio,
        is_external: a.is_external,
      })));
    }
  }, [singlePublication, addMode, data?.id]);

  const validateFields = () => {
    const errors = {};
    if (!authors.length) {
      errors.authors = 'Необходимо добавить хотя бы одного автора';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Сборка тела запроса под API (PublicationWriteBody)
  const buildBody = (values) => ({
    form: values.form,
    name: values.name,
    pub_year: Number(values.pub_year) || 0,
    publish_date: values.publish_date || null,
    season: values.season === '' || values.season === null ? null : Number(values.season),
    subject: values.subject || null,
    review: values.review || 'нет',
    has_copy: !!values.has_copy,
    level: values.level || 'интернет',
    volume: Number(values.volume) || 0,
    volume_pp: parseFloat(values.volume_pp) || 0,
    hours: Number(values.hours) || 0,
    place: values.place || null,
    result_num: values.result_num || null,
    bases: values.bases || [],
    site: values.site || null,
    authors: authors.map(a => (a.is_external ? { fio: a.fio } : { mid: a.mid })),
  });

  const create = async (values) => {
    if (!validateFields()) return;
    const newId = await dispatch(createPublications(buildBody(values), filters));
    if (newId) {
      for (const file of pendingFiles) {
        await dispatch(uploadPublicationFile(newId, file));
      }
      closeModal();
    }
  };

  const update = async (values) => {
    if (!validateFields()) return;
    const success = await dispatch(updatePublications(buildBody(values), filters, data.id));
    success && closeModal();
  };

  const {
    values,
    errors,
    touched,
    submitCount,
    handleSubmit,
    handleChange,
    setFieldValue,
  } = useFormik({
    initialValues: addMode ? emptyValues : { ...emptyValues, ...singlePublication },
    enableReinitialize: true,
    validationSchema,
    onSubmit: addMode ? create : update,
  });

  const error = (fieldName) => (touched[fieldName] || !!submitCount) && errors[fieldName];

  // --- Авторы ---
  const openSelectAuthorModal = () => setOpenAuthorModal(true);
  const closeSelectAuthorModal = () => setOpenAuthorModal(false);

  // Тоггл внутреннего автора (из модалки сотрудников)
  const toggleInternalAuthor = (teacher) => {
    setAuthors(prev => {
      const exists = prev.find(a => !a.is_external && a.mid === teacher.mid);
      if (exists) return prev.filter(a => !(!a.is_external && a.mid === teacher.mid));
      return [...prev, { mid: teacher.mid, fio: teacher.fio, is_external: false }];
    });
    setValidationErrors(prev => ({ ...prev, authors: null }));
  };

  const openIncomeAuthorInput = () => setInAuthor(true);

  const confirmIncomeAuthor = () => {
    const fio = (incomeAuthor || '').trim();
    if (fio.length < 2) return;
    setAuthors(prev => [...prev, { mid: 0, fio, is_external: true }]);
    setIncomeAuthor('');
    setInAuthor(false);
    setValidationErrors(prev => ({ ...prev, authors: null }));
  };

  const removeAuthor = (index) => {
    setAuthors(prev => prev.filter((_, i) => i !== index));
  };

  // Список внутренних авторов для подсветки в модалке сотрудников
  const selectedInternalAuthors = authors.filter(a => !a.is_external);

  // --- Файлы ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (data?.id) {
        await dispatch(uploadPublicationFile(data.id, file));
      } else {
        // Новая несохранённая публикация — копим до сохранения
        setPendingFiles(prev => [...prev, file]);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileDelete = async () => {
    if (!selectedFile) return;
    if (data?.id) {
      await dispatch(deletePublicationFile(data.id, selectedFile.file_name));
    } else {
      setPendingFiles(prev => prev.filter(f => f.name !== selectedFile.file_name));
    }
    setSelectedFile(null);
  };

  const handleFileDownload = (fileName) => {
    if (data?.id) dispatch(downloadPublicationFile(data.id, fileName));
  };

  const handleFilePreview = (fileName) => {
    if (data?.id) dispatch(previewPublicationFile(data.id, fileName));
  };

  // Объединённый список файлов для отображения (сохранённые + отложенные)
  const displayedFiles = data?.id
    ? (publicationFiles || [])
    : pendingFiles.map(f => ({ file_name: f.name, file_size: f.size }));

  return (
    <>
      {openAuthorModal &&
        <TeacherModal
          closeModal={closeSelectAuthorModal}
          data={authorsToAdd}
          height={'430px'}
          handleChangeAuthors={toggleInternalAuthor}
          selectedAuthors={selectedInternalAuthors}
        />
      }
      <Modal
        open={!!data}
        onClose={closeModal}
        showCloseIcon={false}
        height={height}
        center
        closeOnOverlayClick={true}
        closeOnEsc={true}
        classNames={{ modal: styles.custom }}
      >

        <div className={styles.modalLocal}>

          <Tabs>
            <TabList className={styles.tabsBlock}>
              <Tab className={styles.tab} selectedClassName={styles.activeTab}>Основные сведения</Tab>
              <Tab className={styles.tab} selectedClassName={styles.activeTab}>Материалы</Tab>
            </TabList>

            <TabPanel>
              <div className={styles.tabWrapper}>
                <div className={styles.inputsMarginTop}>
                  <div style={{ width: '260px' }}>
                    <CreatableSelect
                      label="Вид публикации"
                      placeholder="Выберите или введите вид"
                      isClearable
                      styles={selectLeftOrigin}
                      options={(publicationForms || []).map(f => ({ value: f, label: f }))}
                      value={values.form ? { value: values.form, label: values.form } : null}
                      onChange={(opt) => setFieldValue('form', opt ? opt.value : '')}
                      error={error('form')}
                    />
                  </div>

                  <Input
                    type="date"
                    label="Дата"
                    name="publish_date"
                    value={values.publish_date || ''}
                    onChange={handleChange}
                    error={error('publish_date')}
                  />

                  <Counter
                    label="Год"
                    name="pub_year"
                    value={values.pub_year}
                    setFieldValue={setFieldValue}
                    error={error('pub_year')}
                  />

                  <Select
                    name="season"
                    label="Полугодие"
                    value={values.season}
                    onChange={(e) => setFieldValue('season', e.target.value === '' ? '' : Number(e.target.value))}
                    style={{ width: '160px' }}
                  >
                    <option value="">—</option>
                    {arrSeason.map((el) => (
                      <option key={el.value} value={el.value}>{el.name}</option>
                    ))}
                  </Select>
                </div>

                <div className={styles.inputsRow} style={{ justifyContent: 'space-between', gap: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <CreatableSelect
                      label="Предмет"
                      placeholder="Выберите или введите предмет"
                      isClearable
                      styles={selectLeftOrigin}
                      options={(publicationsSubject || []).map(s => ({ value: s, label: s }))}
                      value={values.subject ? { value: values.subject, label: values.subject } : null}
                      onChange={(opt) => setFieldValue('subject', opt ? opt.value : '')}
                    />
                  </div>
                  <Select
                    name="review"
                    label="Рецензия"
                    value={values.review}
                    onChange={(e) => setFieldValue('review', e.target.value)}
                    style={{ width: '160px' }}
                  >
                    {arrReview.map((el) => (
                      <option key={el.value} value={el.value}>{el.name}</option>
                    ))}
                  </Select>
                </div>

                <div className={styles.inputsRow} style={{ alignItems: 'flex-start', gap: '16px' }}>
                  <Textarea
                    label="Название работы"
                    name="name"
                    value={values.name}
                    error={error('name')}
                    onChange={handleChange}
                    style={{ width: '600px', minHeight: '60px' }}
                  />
                  <Checkbox
                    name='has_copy'
                    className={styles.checkboxCopy}
                    label='Наличие копии'
                    checked={!!values.has_copy}
                    onChange={() => setFieldValue('has_copy', !values.has_copy)}
                  />
                </div>

                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Авторы</p>
                  {validationErrors.authors && <p style={{ color: '#f44336', fontSize: '12px', margin: '0 0 8px 0' }}>{validationErrors.authors}</p>}
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div className={`${styles.authorsList} ${validationErrors.authors ? styles.hasError : ''}`}>
                      {authors.length === 0 && !inAuthor && (
                        <div className={styles.emptyState}>Авторы не добавлены</div>
                      )}
                      {authors.map((el, index) => (
                        <div key={`${el.is_external ? 'ext' : el.mid}-${index}`} className={styles.authorItem}>
                          <span className={styles.authorName}>
                            {el.fio}{el.is_external ? ' (внешний)' : ''}
                          </span>
                          <button
                            type="button"
                            className={styles.authorRemoveBtn}
                            onClick={() => removeAuthor(index)}
                            title="Удалить автора"
                          >✕</button>
                        </div>
                      ))}
                      {inAuthor && (
                        <div className={styles.authorInputRow}>
                          <Input
                            placeholder='Введите ФИО автора'
                            value={incomeAuthor}
                            onChange={(e) => setIncomeAuthor(e.target.value)}
                            style={{ width: '400px', marginBottom: 0 }}
                          />
                          <button type="button" className={styles.actionBtn} onClick={confirmIncomeAuthor}>Добавить</button>
                          <button type="button" className={`${styles.actionBtn} ${styles.actionBtnDanger}`} onClick={() => { setInAuthor(false); setIncomeAuthor(''); }}>Отмена</button>
                        </div>
                      )}
                    </div>
                    <div className={styles.actionButtons}>
                      <button type="button" className={styles.actionBtn} onClick={openSelectAuthorModal}>
                        + Добавить автора
                      </button>
                      <button type="button" className={styles.actionBtn} onClick={openIncomeAuthorInput}>
                        + Внешний автор
                      </button>
                    </div>
                  </div>
                </div>

                <div className={styles.inputsRow} style={{ gap: '24px', flexWrap: 'wrap' }}>
                  <Select
                    name="level"
                    label="Уровень"
                    value={values.level}
                    onChange={(e) => setFieldValue('level', e.target.value)}
                    style={{ width: '200px' }}
                  >
                    {arrLevel.map((el) => (
                      <option key={el.value} value={el.value}>{el.name}</option>
                    ))}
                  </Select>

                  <Input
                    type='number'
                    label='Объём, страниц'
                    name='volume'
                    value={values.volume}
                    onChange={handleChange}
                    style={{ width: '110px' }}
                  />
                  <Input
                    type='number'
                    step='0.01'
                    label='Объём, п.л.'
                    name='volume_pp'
                    value={values.volume_pp}
                    onChange={handleChange}
                    style={{ width: '110px' }}
                  />
                  <Input
                    type='number'
                    label='Затраты, часов'
                    name='hours'
                    value={values.hours}
                    onChange={handleChange}
                    style={{ width: '110px' }}
                  />
                </div>

                <div className={styles.inputsColumn}>
                  <Textarea
                    label="Место публикации"
                    name="place"
                    value={values.place || ''}
                    onChange={handleChange}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className={styles.inputsColumn}>
                  <Input
                    label="Номер результата"
                    name="result_num"
                    value={values.result_num || ''}
                    onChange={handleChange}
                    style={{ width: '100%' }}
                  />
                </div>

                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Наукометрические базы</p>
                  <div style={{ maxWidth: '500px' }}>
                    <MultiSelect
                      label=''
                      options={arrBases}
                      value={arrBases.filter(o => (values.bases || []).includes(o.value))}
                      onChange={(selected) => setFieldValue('bases', (selected || []).map(o => o.value))}
                    />
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel>
              <div className={styles.tabWrapper}>
                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Ссылка на публикацию</p>
                  <Input
                    label='URL сайта'
                    name='site'
                    value={values.site || ''}
                    style={{ width: '100%', maxWidth: '700px' }}
                    onChange={handleChange}
                    placeholder='https://example.com/publication'
                  />
                </div>

                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Прикреплённые файлы</p>
                  {addMode && <p className={styles.hint}>Файлы будут загружены после сохранения публикации</p>}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '12px' }}>
                    <div className={styles.filesList}>
                      {displayedFiles.length === 0 && (
                        <div className={styles.emptyState}>Файлы не прикреплены</div>
                      )}
                      {displayedFiles.map((file, index) => (
                        <div
                          key={index}
                          className={`${styles.fileItem} ${selectedFile?.file_name === file.file_name ? styles.selected : ''}`}
                          onClick={() => setSelectedFile(file)}
                          onDoubleClick={() => handleFileDownload(file.file_name)}
                          title="Двойной клик для скачивания"
                        >
                          <span className={styles.fileName}>{file.file_name}</span>
                          <span className={styles.fileSize}>{(file.file_size / 1024).toFixed(1)} КБ</span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.actionButtons}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => fileInputRef.current?.click()}
                      >+ Добавить файл</button>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => selectedFile?.file_name && handleFilePreview(selectedFile.file_name)}
                        disabled={!selectedFile || !data?.id}
                      >👁 Просмотр</button>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => selectedFile?.file_name && handleFileDownload(selectedFile.file_name)}
                        disabled={!selectedFile || !data?.id}
                      >⬇ Скачать</button>
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        onClick={handleFileDelete}
                        disabled={!selectedFile}
                      >✕ Удалить</button>
                    </div>
                  </div>
                  {selectedFile && <p className={styles.hint}>Выбран файл: {selectedFile.file_name}</p>}
                </div>
              </div>
            </TabPanel>
          </Tabs>
        </div>
        <ModalFooter handleSubmit={handleSubmit} closeModal={closeModal} />
      </Modal >
    </>
  );
}
